import HTTP from './HTTP.js';
import SymfonyErrorFormatter from './SymfonyErrorFormatter';

class UserServices {
    static constructor() { }

    static setAgencies(agencies) {
        this.agencies = agencies;
        return this;
    }

    static getAgencies() {
        return this.agencies;
    }

    static getUsers(filters, includes, cursor = 0) {
        return HTTP.get(`users`, {
            params: {
                limit: 10,
                filters: filters,
                include: includes,
                cursor
            }
        })
            .then((response) => {
                return {
                    data: response.data.users,
                    meta: response.data.meta
                }
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static exportUserCsv(filters) {
        return HTTP.get(`users/export/csv`, {
            responseType: 'blob',
            params: {
                limit: 5000,
                filters: filters
            }
        }).then((response) => {
            return response.data;
        }).catch((error) => {
            return SymfonyErrorFormatter.clean(error.response);
        });
    }

    static getAgencyUsers() {
        return HTTP.get(`users/agency-users`)
            .then((response) => {
                return {
                    data: response.data.users,
                    meta: response.data.meta
                }
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }
    static searchUsers(query, cursor, limit) {
        return HTTP.get(`users/search`, {
            params: {
                query: query,
                cursor: cursor,
                limit: limit
            }
        })
            .then((response) => {
                return {
                    data: response.data.users,
                    meta: response.data.meta
                }
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static getRoles() {
        return HTTP.get(`users/roles`)
            .then((response) => {
                return response.data;
            }).catch(() => {
                return Promise.reject('Could not get End Point');
            });
    }

    static getClientRoles() {
        return HTTP.get(`users/client-roles`)
            .then((response) => {
                return response.data['users/roleTypes'];
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static forgotPassword(email) {
        return HTTP.post(`users/password/forgot`, { email: email })
            .then((response) => {
                return response.data;
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static resetPassword(token, password) {
        return HTTP.post(`users/password/reset`, { confirmationToken: token, password: password })
            .then((response) => {
                return response.data;
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static getProfile() {
        return HTTP.get(`users/profile`)
            .then((response) => {
                return response.data;
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static getUser(userId, includes) {
        return HTTP.get(`users/${userId}`, {
            params: {
                include: includes
            }
        })
            .then((response) => {
                return response.data;
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static buildPermissions(user) {
        let permissions = [];
        if (typeof user.selectedPermissions !== 'undefined' && typeof this.getAgencies() !== 'undefined') {
            user.selectedPermissions.forEach(sp => {
                if (typeof user.permission !== 'undefined') {
                    let permission = user.permissions.find((p) => {
                        return p.agency === sp;
                    });
                }

                if (typeof permission !== 'undefined') {
                    permissions.push(permission);
                } else {
                    permissions.push({
                        agency: sp,
                        canViewMediaOrders: user.role === 'ROLE_AGENT_USER' ? false : true,
                        canViewPastOrders: user.role === 'ROLE_AGENT_USER' ? false : true,
                        canViewPaymentDetails: user.role === 'ROLE_AGENT_USER' ? false : true,
                        user: user.id ? user.id : null
                    })
                }
            });
        }
        return permissions;
    }

    static updateUser(user) {
        // Only update permissions if in a screen to select permissions. 
        // otherwise it should use the existing permissions structure formatted correctly for the  API
        if (typeof user.selectedPermissions !== 'undefined' && typeof this.getAgencies() !== 'undefined') {
            user.permissions = this.buildPermissions(user);
        } else if (typeof user.permissions !== 'undefined' && typeof user.permissions.data !== 'undefined') {
            user.permissions = user.permissions.data;
        }

        return HTTP.post(`users/${user.id}`, user)
            .then((response) => {
                return response.data;
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static updatePassword(user) {
        return HTTP.post(`users/change-password`, user)
            .then((response) => {
                return response.data;
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static deleteUser(userId) {
        return HTTP.delete(`users/${userId}`)
            .then((response) => {
                return response.data;
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static createUser(user) {
        user.permissions = this.buildPermissions(user);
        user.enabled = false;
        if (typeof user.selectedClient !== 'undefined' && typeof user.selectedClient.id !== 'undefined') {
            user.client = user.selectedClient.id;
        }
        if (typeof user.selectedClients !== 'undefined') {
            user.clients = user.selectedClients;
        }

        return HTTP.post(`users`, user)
            .then((response) => {
                return response.data;
            }).catch(error => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static getLoginVault(code) {
        return HTTP.post(`custom-authenticate/vault/login`, {
            code: code
        })
            .then(response => {
                return response.data;
            })
            .catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static getLogin(credentials) {
        return HTTP.post(`authenticate`, {
            username: credentials.username,
            password: credentials.password,
            'function': "login"
        })
            .then(response => {
                return response.data;
            })
            .catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static refreshLogin() {
        return HTTP.post(`token/refresh`, {
            'refresh_token': localStorage.getItem('refreshToken')
        })
            .then(response => {
                return response.data;
            })
            .catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }

    static archiveUser(userId) {
        return HTTP.delete(`users/archive/${userId}`)
            .then((response) => {
                return response.data;
            }).catch((error) => {
                return SymfonyErrorFormatter.clean(error.response);
            });
    }
}

export default UserServices;
